/**
 * CKEditor FileRepository upload adapter for student images (advanced toolbar).
 *
 * This is not a CKEditor plugin. ImageUpload (in the custom build) owns the
 * toolbar button, paste, and drop. This adapter is registered via extraPlugins
 * only so it can use Auth and lessonManager, which the custom-build tarball
 * cannot see.
 *
 * Upload path matches html-publisher "Upload a File" and the Upload question:
 * POST /api/uploadFileResource. ECMS stores the file in private/userFiles and
 * sets content_item.OWNER_ID from the authenticated user (the student).
 * CKEditor then inserts an img whose src is redirectToResource for that
 * generated filename. Activity mode and teacher presentation only.
 */
import { LessonMode } from '../../Constants';
import DefaultDialog from '../components/dialogs';
import Auth from './AuthService';
import UtilsService from './UtilsService';
import lessonManager from '../managers/LessonManager';

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.gif', '.png'];
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/gif', 'image/x-png'];

// Wider than the allowlist so a disallowed image still reaches the upload
// command, where it is rejected before CKEditor inserts it.
export const studentImageUploadTypes = [
  'jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg+xml', 'avif', 'heic', 'heif'
];

function isAllowedStudentImage(file) {
  const name = (file && file.name ? file.name : '').toLowerCase();
  const dot = name.lastIndexOf('.');
  if (dot >= 0 && dot < name.length - 1) {
    return ALLOWED_EXTENSIONS.includes(name.slice(dot));
  }
  const type = (file && file.type ? file.type : '').toLowerCase();
  return ALLOWED_MIME_TYPES.includes(type);
}

function toFileList(file) {
  if (!file) {
    return [];
  }
  if (Array.isArray(file)) {
    return file;
  }
  if (typeof file.length === 'number') {
    return Array.from(file);
  }
  return [file];
}

function isActivityUploadMode() {
  return lessonManager.playerMode === LessonMode.ACTIVITY_MODE ||
    lessonManager.playerMode === LessonMode.TEACHER_PRESENTATION;
}

function showOutsideActivityDialog() {
  if (DefaultDialog.isVisible()) {
    return;
  }
  DefaultDialog.fire({
    title: 'Cannot upload a file outside of an assignment.',
    showCancelButton: false,
    buttonsStyling: false,
    confirmButtonText: UtilsService.confirmOk(),
    showClass: {
      backdrop: 'swal2-noanimation',
      popup: '',
      icon: ''
    },
    hideClass: {
      popup: ''
    }
  });
}

function showDisallowedImageDialog() {
  if (DefaultDialog.isVisible()) {
    return;
  }
  DefaultDialog.fire({
    title: 'Unable to upload this file type.',
    html: 'Allowed types: JPG, JPEG, GIF, and PNG.',
    showCancelButton: false,
    buttonsStyling: false,
    confirmButtonText: UtilsService.confirmOk(),
    showClass: {
      backdrop: 'swal2-noanimation',
      popup: '',
      icon: ''
    },
    hideClass: {
      popup: ''
    }
  });
}

function mimeFromDataUrl(src) {
  const match = /^data:(image\/[^;,]+)/i.exec(src || '');
  return match ? match[1].toLowerCase() : '';
}

class StudentImageUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  async upload() {
    if (lessonManager.playerMode !== LessonMode.ACTIVITY_MODE &&
      lessonManager.playerMode !== LessonMode.TEACHER_PRESENTATION) {
      throw new Error('Image upload is only available in activity mode');
    }

    const file = await this.loader.file;
    const formData = new FormData();
    formData.append('file', file);

    const uploadResult = await Auth.fetch(`${Auth.ecms}/api/uploadFileResource`, {
      method: 'POST',
      body: formData
    });

    if (uploadResult.status !== 'SUCCESS' && uploadResult.status !== 'WARNING') {
      throw new Error(uploadResult.statusMessage || 'Image upload failed');
    }

    const fileName = uploadResult.userFileGeneratedName || uploadResult.fileName;
    if (!fileName) {
      throw new Error('Image upload failed');
    }

    return {
      default: Auth.getResourceUrlByFileName(fileName)
    };
  }

  abort() {
    // Auth.fetch does not expose an abort handle.
  }
}

function rejectDisallowedUploadFiles(editor) {
  const command = editor.commands.get('uploadImage');
  if (!command) {
    return;
  }
  // Runs before CKEditor inserts the image. Stopping the event keeps the file out of the model.
  command.on('execute', (event, args) => {
    if (!isActivityUploadMode()) {
      event.stop();
      showOutsideActivityDialog();
      return;
    }
    const options = (args && args[0]) || {};
    const files = toFileList(options.file);
    const allowed = files.filter((file) => isAllowedStudentImage(file));
    if (allowed.length !== files.length) {
      showDisallowedImageDialog();
    }
    if (!allowed.length) {
      event.stop();
      return;
    }
    options.file = allowed;
    args[0] = options;
  }, { priority: 'highest' });
}

function rejectDisallowedPastedImages(editor) {
  if (!editor.plugins.has('ClipboardPipeline')) {
    return;
  }
  editor.plugins.get('ClipboardPipeline').on('inputTransformation', (event, data) => {
    if (!isActivityUploadMode()) {
      const images = [];
      for (const entry of editor.editing.view.createRangeIn(data.content)) {
        const node = entry.item;
        if (node && node.is && node.is('element', 'img') && mimeFromDataUrl(node.getAttribute('src'))) {
          images.push(node);
        }
      }
      if (!images.length) {
        return;
      }
      images.forEach((node) => {
        if (typeof node._remove === 'function') {
          node._remove();
        }
      });
      showOutsideActivityDialog();
      return;
    }
    const rejected = [];
    for (const entry of editor.editing.view.createRangeIn(data.content)) {
      const node = entry.item;
      if (!node || !node.is || !node.is('element', 'img')) {
        continue;
      }
      const mime = mimeFromDataUrl(node.getAttribute('src'));
      if (mime && !ALLOWED_MIME_TYPES.includes(mime)) {
        rejected.push(node);
      }
    }
    if (!rejected.length) {
      return;
    }
    rejected.forEach((node) => {
      if (typeof node._remove === 'function') {
        node._remove();
      }
    });
    showDisallowedImageDialog();
  }, { priority: 'highest' });
}

function blockUploadButtonOutsideActivity(editor) {
  const factory = editor.ui.componentFactory;
  const originalCreate = factory.create.bind(factory);
  factory.create = (name) => {
    const component = originalCreate(name);
    if (String(name).toLowerCase() === 'uploadimage' && component && !component._studentUploadGuard) {
      component._studentUploadGuard = true;
      // FileDialogButtonView opens the picker on execute. Stop that outside an activity.
      component.on('execute', (event) => {
        if (isActivityUploadMode()) {
          return;
        }
        event.stop();
        showOutsideActivityDialog();
      }, { priority: 'highest' });
    }
    return component;
  };
}

export default function registerStudentImageUploadAdapter(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return new StudentImageUploadAdapter(loader);
  };

  blockUploadButtonOutsideActivity(editor);

  editor.once('ready', () => {
    rejectDisallowedUploadFiles(editor);
    rejectDisallowedPastedImages(editor);
  });
}
